import React, {
    createContext,
    useContext,
    useState,
    ReactNode,
    useMemo,
} from 'react'
import { Address } from '../../models/types'
import { DateTime } from 'luxon'

// Define the available timezone types
export enum TimezoneType {
    PROPERTY = 'property',
    USER = 'user',
    CUSTOM = 'custom',
}

// Interface for the Timezone Context state
export interface TimezoneState {
    // Current active timezone
    currentTimezone: string

    // Type of the current timezone
    timezoneType: TimezoneType

    // Property timezone information if available
    propertyTimezone: string | null
    propertyAddress: Address | null

    // Methods to update timezone
    setCurrentTimezone: (timezone: string) => void
    setTimezoneType: (type: TimezoneType) => void
    setPropertyTimezone: (timezone: string, address: Address | null) => void

    // Method to reset to browser's timezone
    useUserTimezone: () => void

    // Method to use property timezone
    usePropertyTimezone: () => void

    // Method to format a date according to the current timezone
    formatDate: (date: Date | string, format?: string) => string

    // Method to convert a date to the current timezone
    convertToTimezone: (date: Date | string) => Date

    // Add these new methods:
    getTimezoneAbbr: (timezone: string) => string
}

// Create the Timezone Context with default values
export const TimezoneContext = createContext<TimezoneState | undefined>(
    undefined,
)

// Add the abbreviations map to the context file
// Map of common timezone IDs to their 3-letter abbreviations
const timezoneAbbreviations: Record<
    string,
    { standard: string; daylight: string }
> = {
    'America/New_York': { standard: 'EST', daylight: 'EDT' },
    'America/Chicago': { standard: 'CST', daylight: 'CDT' },
    'America/Denver': { standard: 'MST', daylight: 'MDT' },
    'America/Los_Angeles': { standard: 'PST', daylight: 'PDT' },
    'America/Phoenix': { standard: 'MST', daylight: 'MST' }, // Phoenix doesn't use DST
    'America/Anchorage': { standard: 'AKST', daylight: 'AKDT' },
    'Pacific/Honolulu': { standard: 'HST', daylight: 'HST' }, // Hawaii doesn't use DST
    'America/Halifax': { standard: 'AST', daylight: 'ADT' },
    'Europe/London': { standard: 'GMT', daylight: 'BST' },
    'Europe/Paris': { standard: 'CET', daylight: 'CEST' },
    'Europe/Berlin': { standard: 'CET', daylight: 'CEST' },
    'Europe/Athens': { standard: 'EET', daylight: 'EEST' },
    'Asia/Tokyo': { standard: 'JST', daylight: 'JST' }, // Japan doesn't use DST
    'Asia/Shanghai': { standard: 'CST', daylight: 'CST' }, // China doesn't use DST
    'Asia/Kolkata': { standard: 'IST', daylight: 'IST' }, // India doesn't use DST
    'Australia/Sydney': { standard: 'AEST', daylight: 'AEDT' },
    UTC: { standard: 'UTC', daylight: 'UTC' },
}

// Create a provider component for the Timezone Context
export const TimezoneProvider: React.FC<{ children: ReactNode }> = ({
    children,
}) => {
    // Get browser's timezone
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone

    // State for current timezone
    const [currentTimezone, _setCurrentTimezone] = useState<string>(
        browserTimezone,
    )
    const [timezoneType, _setTimezoneType] = useState<TimezoneType>(
        TimezoneType.USER,
    )
    const [propertyTimezone, _setPropertyTimezone] = useState<string | null>(
        null,
    )
    const [propertyAddress, setPropertyAddress] = useState<Address | null>(null)

    // Method to set current timezone
    const setCurrentTimezone = (timezone: string) => {
        _setCurrentTimezone(timezone)
        _setTimezoneType(TimezoneType.CUSTOM)
    }

    // Method to set timezone type
    const setTimezoneType = (type: TimezoneType) => {
        _setTimezoneType(type)

        // Update the current timezone based on the type
        if (type === TimezoneType.USER) {
            _setCurrentTimezone(browserTimezone)
        } else if (type === TimezoneType.PROPERTY && propertyTimezone) {
            _setCurrentTimezone(propertyTimezone)
        }
    }

    // Method to set property timezone
    const setPropertyTimezone = (timezone: string, address: Address | null) => {
        _setPropertyTimezone(timezone)
        setPropertyAddress(address)

        // Always switch to property timezone when it's set
        _setCurrentTimezone(timezone)
        _setTimezoneType(TimezoneType.PROPERTY)
    }

    // Method to use user's timezone
    const useUserTimezone = () => {
        _setCurrentTimezone(browserTimezone)
        _setTimezoneType(TimezoneType.USER)
    }

    // Method to use property timezone
    const usePropertyTimezone = () => {
        if (propertyTimezone) {
            _setCurrentTimezone(propertyTimezone)
            _setTimezoneType(TimezoneType.PROPERTY)
        }
    }

    // Method to format a date according to the current timezone
    const formatDate = (date: Date | string, format?: string): string => {
        const dateObj = typeof date === 'string' ? new Date(date) : date

        try {
            // Default format options
            const options: Intl.DateTimeFormatOptions = {
                timeZone: currentTimezone,
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
            }

            if (format === 'date-only') {
                delete options.hour
                delete options.minute
            } else if (format === 'time-only') {
                delete options.year
                delete options.month
                delete options.day
            }

            return new Intl.DateTimeFormat('en-US', options).format(dateObj)
        } catch (error) {
            console.error('Error formatting date:', error)
            return dateObj.toLocaleString()
        }
    }

    // Method to convert a date to the current timezone
    const convertToTimezone = (date: Date | string): Date => {
        const dateObj = typeof date === 'string' ? new Date(date) : date

        // The Date object doesn't directly support timezone conversion
        // For proper timezone handling, a library like Luxon or date-fns-tz would be better
        // For now, this is a simplified approach that might not account for all timezone nuances
        return dateObj
    }

    // Get formatted timezone abbreviation that accounts for DST
    const getTimezoneAbbr = (timezone: string): string => {
        // First check if we have a predefined abbreviation
        if (timezoneAbbreviations[timezone]) {
            // Check if DST is active in the specific timezone being abbreviated
            const now = DateTime.now().setZone(timezone)
            const isDST = now.isInDST

            return isDST
                ? timezoneAbbreviations[timezone].daylight
                : timezoneAbbreviations[timezone].standard
        }

        // For timezones not in our list, try to generate a reasonable abbreviation
        // First try to get the last part after the slash
        const lastPart = timezone.split('/').pop()
        if (!lastPart) return timezone

        // If it's a single word, get the first 3 letters
        if (!lastPart.includes('_')) {
            return lastPart.substring(0, 3).toUpperCase()
        }

        // If it's multiple words (with underscores), get first letter of each word
        const words = lastPart.split('_')
        if (words.length >= 3) {
            return words
                .map((word) => word[0])
                .join('')
                .toUpperCase()
        } else if (words.length === 2) {
            // With 2 words, use first 2 letters of first word + first letter of second word
            return (words[0].substring(0, 2) + words[1][0]).toUpperCase()
        }

        // Fallback to first 3 characters
        return lastPart.substring(0, 3).toUpperCase()
    }

    // Create the context value
    const value = useMemo(
        () => ({
            currentTimezone,
            timezoneType,
            propertyTimezone,
            propertyAddress,
            setCurrentTimezone,
            setTimezoneType,
            setPropertyTimezone,
            useUserTimezone,
            usePropertyTimezone,
            formatDate,
            convertToTimezone,
            getTimezoneAbbr,
        }),
        [currentTimezone, timezoneType, propertyTimezone, propertyAddress],
    )

    return (
        <TimezoneContext.Provider value={value}>
            {children}
        </TimezoneContext.Provider>
    )
}

// Custom hook to use the Timezone Context
export const useTimezone = (): TimezoneState => {
    const context = useContext(TimezoneContext)

    if (context === undefined) {
        throw new Error('useTimezone must be used within a TimezoneProvider')
    }

    return context
}
